import React, { useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { LicensePlan } from "../../../store/actions/PlanActions";
import { showModal } from "../../../store/actions/modalActions";

/* ─────────────────────────────────────────────────────────
   Section & AI Feature → human-readable labels
   ───────────────────────────────────────────────────────── */
const SECTION_LABELS: Record<string, string> = {
  matters: "Case Management",
  clients: "Client Management",
  tasks: "Tasks & Deadlines",
  files: "Document Management",
  research: "AI Legal Research",
  tables: "AI Due Diligence Tables",
  analytics: "Analytics & Reporting",
  data_sources: "Knowledge Base (RAG)",
  templates: "Document Templates",
  client_pipeline: "Client Pipeline & AI Intake",
  finance: "Finance & Billing",
  people: "People Management",
  departments: "Departments",
  teams: "Teams",
  calendar: "Calendar",
  messages: "Message Center",
};

const AI_FEATURE_LABELS: Record<string, string> = {
  drafts: "AI Document Drafts",
  insights: "AI Matter Insights",
  research: "AI Legal Research",
  tables_ai: "AI Table Analysis",
  onboarding_ai: "AI Client Onboarding",
  template_ai: "AI Template Generation",
  data_source_indexing: "Knowledge Base Indexing",
};

/* ─────────────────────────────────────────────────────────
   Find which plan/addon unlocks a given section or feature
   ───────────────────────────────────────────────────────── */
function findUnlockingPlan(
  section: string | null,
  aiFeature: string | null,
  plans: LicensePlan[],
  addons: LicensePlan[]
): { plan: LicensePlan | null; isAddon: boolean } {
  for (const addon of addons) {
    if (
      (section && addon.feature_gates?.sections?.includes(section)) ||
      (aiFeature && addon.feature_gates?.ai_features?.includes(aiFeature))
    ) {
      return { plan: addon, isAddon: true };
    }
  }
  for (const plan of plans) {
    if (
      (section && plan.feature_gates?.sections?.includes(section)) ||
      (aiFeature && plan.feature_gates?.ai_features?.includes(aiFeature))
    ) {
      return { plan, isAddon: false };
    }
  }
  return { plan: null, isAddon: false };
}

/* ─────────────────────────────────────────────────────────
   LicenseErrorHandler — global listener for 403 licensing errors
   from the axios interceptor. Shows the upgrade modal.
   
   Mount once in App.tsx or your top-level layout.
   ───────────────────────────────────────────────────────── */
export default function LicenseErrorHandler() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const plans = useSelector((state: RootState) => state.license?.plans ?? []);
  const addons = useSelector((state: RootState) => state.license?.addons ?? []);

  const handleLicenseError = useCallback(
    (event: Event) => {
      // Skip if the LicenseOverlay is already showing on this page
      if ((window as any).__ponsLicenseOverlayActive) return;

      const detail = (event as CustomEvent).detail;
      if (!detail?.code) return;

      const { code, required_section, required_feature, limit_key, current_count, limit_value } = detail;

      let title = "Upgrade Required";
      let description = detail.message || "";

      switch (code) {
        case "LICENSE_EXPIRED": {
          title = "License Expired";
          description =
            "Your organization's license has expired. Please renew to continue using PONS.";
          break;
        }
        case "SECTION_NOT_LICENSED": {
          const sectionLabel = SECTION_LABELS[required_section] || required_section || "this feature";
          const { plan, isAddon } = findUnlockingPlan(required_section, null, plans, addons);
          description = `Your current plan does not include access to <strong>${sectionLabel}</strong>. ${
            isAddon && plan
              ? `Add the <strong>${plan.display_name}</strong> add-on to unlock this feature.`
              : plan
              ? `Upgrade to <strong>${plan.display_name}</strong> to unlock this feature.`
              : "Please upgrade your plan to access this feature."
          }`;
          title = sectionLabel;
          break;
        }
        case "AI_FEATURE_NOT_LICENSED": {
          const featureLabel = AI_FEATURE_LABELS[required_feature] || required_feature || "this AI feature";
          const { plan, isAddon } = findUnlockingPlan(null, required_feature, plans, addons);
          description = `Your current plan does not include <strong>${featureLabel}</strong>. ${
            isAddon && plan
              ? `Add the <strong>${plan.display_name}</strong> add-on to unlock this capability.`
              : plan
              ? `Upgrade to <strong>${plan.display_name}</strong> to unlock this capability.`
              : "Please upgrade your plan to access this capability."
          }`;
          title = featureLabel;
          break;
        }
        case "LICENSE_READ_ONLY": {
          title = "Read-Only Mode";
          description =
            "Your organization's license has expired. You can view existing data but cannot make changes. Renew your license to restore full access.";
          break;
        }
        case "USAGE_LIMIT_REACHED": {
          const limitLabel = (limit_key || "").replace("max_", "").replace(/_/g, " ");
          title = "Usage Limit Reached";
          description = `You've used <strong>${current_count || "all"}</strong> of your <strong>${limit_value || ""}</strong> ${limitLabel}. Upgrade your plan to increase this limit.`;
          break;
        }
        case "NO_BASE_SEAT": {
          title = "No Active Seat";
          description =
            "You don't currently have a seat on your organization's plan. Ask your admin to assign you a seat to access this feature.";
          break;
        }
      }

      dispatch(
        showModal({
          type: "license-request",
          showModal: true,
          message: title,
          subMsg: description,
          title,
          onConfirm: () => {
            history.push("/account?tab=plans");
          },
        })
      );
    },
    [dispatch, history, plans, addons]
  );

  useEffect(() => {
    window.addEventListener("license-error", handleLicenseError);
    return () => {
      window.removeEventListener("license-error", handleLicenseError);
    };
  }, [handleLicenseError]);

  return null; // This component renders nothing — it's just an event listener
}
